/**
 * Searchable multi- or single-select with optional Enter-to-create: adds the search text
 * when it is not an exact duplicate of an existing option (substring matches do not block).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryBuilderOption } from "../../data-model/queryBuilder";
import ChevronDown from "../../svg/chevron-down";
import { CrossIcon } from "../../svg/cross-icon";

interface MultiSelectTagInputProps {
  id: string;
  options: QueryBuilderOption[];
  /** Selected options (with labels). Derive from options using current values, e.g. options.filter(o => values.includes(o.value)) */
  selectedOptions: QueryBuilderOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** When true (default), multiple options can be selected with checkboxes. When false, only one option can be selected with radio buttons. */
  isMultiSelect?: boolean;
  /**
   * When true (default), Enter adds the trimmed search as a value if it is not already selected:
   * exact match to an option selects that option; otherwise the text is added as a custom tag.
   */
  allowCreateOnEnter?: boolean;
}

const MultiSelectTagInput = ({
  id,
  options,
  selectedOptions,
  onChange,
  placeholder = "Select values...",
  isMultiSelect = true,
  allowCreateOnEnter = true,
}: MultiSelectTagInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValues = selectedOptions.map((o) => o.value);

  const visibleOptions = useMemo(() => {
    const t = filterText.trim().toLowerCase();
    if (!t) {
      return options;
    }
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(t) ||
        o.value.toLowerCase().includes(t),
    );
  }, [options, filterText]);

  /**
   * Commits the search field on Enter: selects an option when the query exactly matches
   * a value/label; otherwise appends the trimmed query as a custom tag (multi) or sole
   * value (single). Partial/substring matches alone no longer block free-text create.
   *
   * @param rawQuery - Current search field text
   */
  const tryCommitCreateOnEnter = (rawQuery: string) => {
    if (!allowCreateOnEnter) {
      return;
    }
    const trimmed = rawQuery.trim();
    if (!trimmed) {
      return;
    }
    if (
      selectedValues.some((v) => v.toLowerCase() === trimmed.toLowerCase())
    ) {
      setFilterText("");
      return;
    }

    const exactOption = options.find(
      (o) =>
        o.value.toLowerCase() === trimmed.toLowerCase() ||
        o.label.toLowerCase() === trimmed.toLowerCase(),
    );

    if (exactOption) {
      if (isMultiSelect) {
        if (!selectedValues.includes(exactOption.value)) {
          onChange([...selectedValues, exactOption.value]);
        }
      } else {
        onChange([exactOption.value]);
        setIsOpen(false);
      }
      setFilterText("");
      return;
    }

    if (isMultiSelect) {
      onChange([...selectedValues, trimmed]);
    } else {
      onChange([trimmed]);
      setIsOpen(false);
    }
    setFilterText("");
  };

  const handleToggle = (val: string) => {
    if (isMultiSelect) {
      if (selectedValues.includes(val)) {
        onChange(selectedValues.filter((v) => v !== val));
      } else {
        onChange([...selectedValues, val]);
      }
    } else {
      // Single select: select this value, or clear if already selected
      if (selectedValues.includes(val)) {
        onChange([]);
      } else {
        onChange([val]);
      }
    }
  };

  const handleRemove = (val: string) => {
    onChange(selectedValues.filter((v) => v !== val));
  };

  const trimmedFilter = filterText.trim();
  const exactMatchOption =
    trimmedFilter === ""
      ? undefined
      : options.find(
          (o) =>
            o.value.toLowerCase() === trimmedFilter.toLowerCase() ||
            o.label.toLowerCase() === trimmedFilter.toLowerCase(),
        );
  const filterAlreadySelected =
    trimmedFilter !== "" &&
    selectedValues.some(
      (v) => v.toLowerCase() === trimmedFilter.toLowerCase(),
    );
  const showEnterToAddHint =
    allowCreateOnEnter &&
    trimmedFilter !== "" &&
    exactMatchOption == null &&
    !filterAlreadySelected;

  const selectionScope = filterText.trim() ? visibleOptions : options;
  const allSelected =
    selectionScope.length > 0 &&
    selectionScope.every((o) => selectedValues.includes(o.value));
  const someSelectedInScope =
    selectionScope.some((o) => selectedValues.includes(o.value)) &&
    !allSelected;

  const handleSelectAll = () => {
    const scopeValues = selectionScope.map((o) => o.value);
    if (allSelected) {
      onChange(selectedValues.filter((v) => !scopeValues.includes(v)));
    } else {
      onChange([...new Set([...selectedValues, ...scopeValues])]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFilterText("");
      return;
    }
    searchInputRef.current?.focus();
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative" id={id}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        className="flex cursor-pointer items-center justify-between gap-2 rounded-e rounded-s border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:border-gray-400"
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {selectedOptions.length === 0 && (
            <span className="text-gray-400">{placeholder}</span>
          )}
          {selectedOptions.map((opt) => (
            <div
              key={opt.value}
              className="flex flex-row gap-1 rounded-e rounded-s border border-primaryGray-1 bg-primaryGray-16 px-2 py-0.5 text-xs font-medium text-primaryGray-1"
            >
              <div className="flex items-center justify-center pb-0.5">
                {opt.label}
              </div>
              <div
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(opt.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleRemove(opt.value);
                  }
                }}
                className="flex cursor-pointer items-center justify-center rounded hover:bg-primaryGray-16 hover:text-primaryGray-1"
              >
                <CrossIcon width="12" height="12" fillColor="#000000" />
              </div>
            </div>
          ))}
        </div>
        <ChevronDown
          rotate={isOpen ? 180 : 0}
          stroke="#6b7280"
          width={12}
          height={12}
          className="flex-shrink-0"
        />
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-auto rounded-sm border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              ref={searchInputRef}
              id={`${id}-search`}
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  tryCommitCreateOnEnter(filterText);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search…"
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-primaryGray-1"
              aria-label="Filter options"
            />
            {showEnterToAddHint && (
              <div className="mt-1 text-xs text-gray-500">
                {visibleOptions.length === 0
                  ? "No matches. "
                  : "No exact match. "}
                Press Enter to add &quot;{trimmedFilter}&quot;.
              </div>
            )}
          </div>
          {isMultiSelect && selectionScope.length > 0 && (
            <label className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <input
                type="checkbox"
                ref={(el) => {
                  if (el) el.indeterminate = someSelectedInScope;
                }}
                checked={allSelected}
                onChange={handleSelectAll}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-300 accent-primaryGray-1"
                aria-label="Select all"
              />
              <span>Select all</span>
            </label>
          )}
          {visibleOptions.map((opt) => {
            const isChecked = selectedValues.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <input
                  type={isMultiSelect ? "checkbox" : "radio"}
                  name={isMultiSelect ? undefined : id}
                  checked={isChecked}
                  onChange={() => handleToggle(opt.value)}
                  onClick={(e) => e.stopPropagation()}
                  className={`${isMultiSelect ? "h-4 w-4 rounded" : "h-4 w-4"} border-gray-300 accent-primaryGray-1`}
                  aria-label={`Select ${opt.label}`}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectTagInput;
